import { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Expand } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { MAPBOX_TOKEN } from "@/config/mapbox";
import { hasPlottableCoordinates } from "@/lib/coordinates";

interface RoomLocationMapProps {
  /** Property latitude (properties.lat). */
  lat: number;
  /** Property longitude (properties.lng). */
  lng: number;
  /**
   * Stable seed for the privacy offset — the room id. The same room always
   * gets the same displayed point, so the pin never jumps between visits.
   */
  seed: number;
}

/** Metres per degree of latitude (close enough at German latitudes). */
const METERS_PER_DEGREE_LAT = 111_320;

/**
 * Shift the real coordinates by a small, stable amount.
 *
 * The public room page deliberately hides the house number (COL-218,
 * `resolvePublicStreet`), so dropping a pin on the exact building would undo
 * that. Like Airbnb, we show the right neighbourhood without the doorstep:
 * the pin sits 110–200 m from the real point, in a direction derived from the
 * room id (golden-angle spacing, so neighbouring ids don't share a bearing).
 */
function offsetForPrivacy(
  lat: number,
  lng: number,
  seed: number
): [number, number] {
  const bearing = ((seed * 137.508) % 360) * (Math.PI / 180);
  const distance = 110 + ((seed * 61) % 90);
  const deltaLat = (distance * Math.cos(bearing)) / METERS_PER_DEGREE_LAT;
  const deltaLng =
    (distance * Math.sin(bearing)) /
    (METERS_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180));
  return [lng + deltaLng, lat + deltaLat];
}

/** The house pin, as a detached element Mapbox can own. */
function createPinElement(): HTMLDivElement {
  const element = document.createElement("div");
  element.setAttribute("role", "img");
  element.style.cssText = "color: hsl(var(--primary)); line-height: 0;";
  element.innerHTML = `
    <svg width="40" height="48" viewBox="0 0 40 48" fill="none" aria-hidden="true">
      <path d="M20 47C7 30.5 8.6 17 20 17s13 13.5 0 30Z" fill="currentColor"/>
      <circle cx="20" cy="26" r="13" fill="currentColor"/>
      <path d="M20 19.5 26 25v7h-12v-7Z" fill="#fff"/>
      <rect x="18" y="27" width="4" height="5" fill="currentColor"/>
    </svg>
  `;
  return element;
}

/**
 * "Where you'll be" — a single-room location map for the public room page
 * (COL-48). Heading + map only; the neighbourhood copy lives in the existing
 * "About this home" intro further up the page.
 *
 * The inline map is a preview: panning and zooming are off, and clicking it
 * opens the same location in a full-size dialog where the visitor can explore
 * freely. That split is deliberate — a small map that swallows drags and
 * scrolls is worse than one that opens on click, and it removes the ambiguity
 * between "drag the map" and "click to expand".
 *
 * Renders nothing when the map can't be drawn (no Mapbox token configured, or
 * the property has no coordinates) — this is an unauthenticated marketing
 * page, so a broken frame or an admin-facing setup message is worse than no
 * section at all.
 */
const RoomLocationMap = ({ lat, lng, seed }: RoomLocationMapProps) => {
  const { t, language } = useLanguage();
  const previewContainer = useRef<HTMLDivElement>(null);
  const previewMap = useRef<mapboxgl.Map | null>(null);
  const previewPin = useRef<HTMLDivElement | null>(null);

  const expandedContainer = useRef<HTMLDivElement>(null);
  const expandedMap = useRef<mapboxgl.Map | null>(null);
  const expandedPin = useRef<HTMLDivElement | null>(null);

  const [expanded, setExpanded] = useState(false);

  // Not `Number.isFinite` alone: `properties.lat`/`lng` are NOT NULL, so an
  // ungeocoded property stores 0/0 and a finiteness check happily centres the
  // map on null island in the Gulf of Guinea.
  const hasCoordinates = hasPlottableCoordinates(lat, lng);

  const markerLabel = t(
    "property.location.markerLabel",
    language === "de"
      ? "Ungefährer Standort des Zimmers"
      : "Approximate location of this room"
  );

  const expandLabel = t(
    "property.location.expand",
    language === "de" ? "Karte vergrößern" : "Open a larger map"
  );

  // Build the map once per room. `t` and `language` are deliberately absent
  // from the dependencies: LanguageProvider hands out a fresh `t` on every
  // translations refetch (staleTime 0 + refetchOnWindowFocus), so depending
  // on it here would tear down and rebuild the whole Mapbox instance — new
  // tile requests and a visible flicker — every time the window regains
  // focus. The label is refreshed separately below, the same split
  // PropertyMapView uses.
  useEffect(() => {
    if (!previewContainer.current || previewMap.current) return;
    if (!MAPBOX_TOKEN || !hasCoordinates) return;

    const center = offsetForPrivacy(lat, lng, seed);

    mapboxgl.accessToken = MAPBOX_TOKEN;
    previewMap.current = new mapboxgl.Map({
      container: previewContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center,
      zoom: 14,
      // A preview, not a controllable map — every gesture is off so the click
      // target is unambiguous and the page never loses a scroll to it.
      interactive: false,
    });

    const element = createPinElement();
    const marker = new mapboxgl.Marker({ element, anchor: "bottom" })
      .setLngLat(center)
      .addTo(previewMap.current);
    previewPin.current = element;

    return () => {
      marker.remove();
      previewPin.current = null;
      previewMap.current?.remove();
      previewMap.current = null;
    };
  }, [lat, lng, seed, hasCoordinates]);

  // The expanded map is built only once the dialog is actually open: its
  // container doesn't exist before that, and Mapbox measures the element on
  // construction — building it against a hidden node yields a 0×0 canvas.
  const mountExpandedMap = useCallback(
    (node: HTMLDivElement | null) => {
      expandedContainer.current = node;
      if (!node || expandedMap.current) return;
      if (!MAPBOX_TOKEN || !hasCoordinates) return;

      const center = offsetForPrivacy(lat, lng, seed);

      mapboxgl.accessToken = MAPBOX_TOKEN;
      expandedMap.current = new mapboxgl.Map({
        container: node,
        style: "mapbox://styles/mapbox/streets-v12",
        center,
        zoom: 15,
      });

      expandedMap.current.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        "top-right"
      );

      const element = createPinElement();
      new mapboxgl.Marker({ element, anchor: "bottom" })
        .setLngLat(center)
        .addTo(expandedMap.current);
      expandedPin.current = element;
      element.setAttribute("aria-label", markerLabel);
    },
    // `markerLabel` is read once at mount and refreshed by the effect below,
    // for the same reason the preview map ignores it: a new `t` must not
    // rebuild the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lat, lng, seed, hasCoordinates]
  );

  // Tear the expanded map down on close so reopening starts at the pin again
  // rather than wherever the visitor last panned to — and, via the same
  // cleanup, on unmount while the dialog is still open, so navigating away
  // mid-view doesn't leak the Mapbox GL instance.
  useEffect(() => {
    if (!expanded) return;
    return () => {
      expandedMap.current?.remove();
      expandedMap.current = null;
      expandedPin.current = null;
    };
  }, [expanded]);

  // Keep the markers' labels in the current language without touching the maps.
  useEffect(() => {
    previewPin.current?.setAttribute("aria-label", markerLabel);
    expandedPin.current?.setAttribute("aria-label", markerLabel);
  }, [markerLabel]);

  if (!MAPBOX_TOKEN || !hasCoordinates) return null;

  const approximateNote = t(
    "property.location.approximateNote",
    language === "de"
      ? "Ungefährer Standort. Die genaue Adresse erhältst du, sobald deine Buchung bestätigt ist."
      : "Approximate location. The exact address is shared once your booking is confirmed."
  );

  const heading = t(
    "property.location.heading",
    language === "de" ? "Wo du wohnen wirst" : "Where you'll be"
  );

  return (
    <Card className="p-6">
      <h2 className="text-2xl font-bold text-foreground mb-4">{heading}</h2>

      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label={expandLabel}
        className="group relative block h-[320px] w-full overflow-hidden rounded-xl border border-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:h-[380px]"
      >
        <div ref={previewContainer} className="h-full w-full" aria-hidden />
        {/* Sits above the canvas so the whole tile area is one click target —
            Mapbox's own canvas would otherwise eat the event. */}
        <span className="absolute inset-0" />
        <span className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1.5 rounded-lg bg-background/95 px-3 py-2 text-sm font-medium text-foreground shadow-md transition-transform group-hover:scale-105">
          <Expand className="h-4 w-4" aria-hidden />
          {expandLabel}
        </span>
      </button>

      <p className="mt-3 text-sm text-muted-foreground">{approximateNote}</p>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-5xl p-0">
          <div className="p-6 pb-3">
            <DialogTitle className="text-xl font-bold">{heading}</DialogTitle>
            <DialogDescription className="mt-1 text-sm">
              {approximateNote}
            </DialogDescription>
          </div>
          <div
            ref={mountExpandedMap}
            className="h-[60vh] w-full overflow-hidden rounded-b-lg"
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RoomLocationMap;
